import React from 'react';
import { Box, Typography, Chip, Avatar } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

export function ProductSearchOption({
  props,
  option,
  currency = 'USD',
  decimals = 2,
  isSelected = false,
  priceMode = 'selling', // 'selling' | 'cost' | 'wholesale'
}) {
  if (!option || typeof option === 'string') {
    return (
      <Box component="li" {...props} key={props?.key || option}>
        {option}
      </Box>
    );
  }

  const { key, ...otherProps } = props || {};
  const sellingPrice = Number(option.sellingPrice || 0);
  const costPrice = Number(option.costPrice || 0);
  const wholesalePrice = option.wholesalePrice ? Number(option.wholesalePrice) : null;
  const stock = option.currentStock ?? option.openingStock ?? null;
  const hasImage = Boolean(option.imageUrl);
  const unit = option.baseUnit || 'pcs';

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const baseUrl = apiUrl.replace(/\/api\/?$/, '');

  const displayPrice = priceMode === 'cost' ? costPrice : (priceMode === 'wholesale' && wholesalePrice != null ? wholesalePrice : sellingPrice);
  const priceColor = priceMode === 'cost' ? '#2563EB' : '#059669';
  const priceBg = priceMode === 'cost' ? '#EFF6FF' : '#ECFDF5';
  const priceBorder = priceMode === 'cost' ? '#BFDBFE' : '#A7F3D0';

  return (
    <Box
      component="li"
      key={option.id || key}
      {...otherProps}
      sx={{
        p: 1.25,
        px: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        borderRadius: 1.5,
        mb: 0.5,
        borderBottom: '1px solid #F1F5F9',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: '#F0F9FF !important',
        },
        '&[aria-selected="true"]': {
          bgcolor: '#E0F2FE !important',
        },
      }}
    >
      {/* Left side: Thumbnail / Icon + Name + Barcode / SKU / Brand / Category */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        {hasImage ? (
          <Box
            component="img"
            src={option.imageUrl.startsWith('http') ? option.imageUrl : `${baseUrl}${option.imageUrl}`}
            alt={option.name}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              objectFit: 'cover',
              border: '1px solid #E2E8F0',
              bgcolor: '#F8FAFC',
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              bgcolor: '#F1F5F9',
              color: '#475569',
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 22 }} />
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.92rem',
                color: '#1E293B',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.name}
            </Typography>
            {isSelected && (
              <Chip
                label="In Bill"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, px: 0.2 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
            {option.barcode && (
              <Chip
                icon={<QrCodeScannerIcon sx={{ fontSize: '12px !important' }} />}
                label={option.barcode}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: '#F1F5F9',
                  color: '#475569',
                  fontWeight: 500,
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            )}
            {option.sku && option.sku !== option.barcode && (
              <Chip
                label={`SKU: ${option.sku}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  color: '#64748B',
                }}
              />
            )}
            {option.brand && (
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                • {option.brand}
              </Typography>
            )}
            {option.category?.name && (
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                ({option.category.name})
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right side: Price & Stock */}
      <Box sx={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.5,
            px: 1.25,
            py: 0.35,
            borderRadius: 1.5,
            bgcolor: priceBg,
            border: `1px solid ${priceBorder}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              color: priceColor,
              lineHeight: 1,
            }}
          >
            {displayPrice.toFixed(decimals)}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: priceColor,
              opacity: 0.9,
            }}
          >
            {currency}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.2 }}>
          {priceMode === 'selling' && wholesalePrice != null && wholesalePrice > 0 && wholesalePrice !== sellingPrice && (
            <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.72rem' }}>
              WS: {wholesalePrice.toFixed(decimals)} {currency}
            </Typography>
          )}

          {stock != null && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                color: Number(stock) > 0 ? '#15803D' : '#DC2626',
              }}
            >
              {Number(stock) > 0 ? `Stock: ${stock} ${unit}` : 'Out of Stock'}
            </Typography>
          )}
          {stock == null && unit && (
            <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.72rem' }}>
              /{unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ProductSearchOption;
